<template>
  <v-dialog
    v-model="visible"
    persistent
    max-width="800"
    content-class="po-quality-grading-dialog"
    @click:outside="close"
  >
    <v-card class="po-quality-grading-card">
      <v-toolbar color="primary" flat height="48" class="white--text">
        <v-toolbar-title class="font-weight-bold text-subtitle-1">
          {{ $t("memberDataSection.purchaseOrderDetail.qualityGradingTitle") }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon class="white--text" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0 po-quality-grading-card__body">
        <div class="px-6 pt-5 pb-2">
          <div class="text-caption text--secondary">
            {{ $t("memberDataSection.greenCoffee.totalQuantity") }}
          </div>
          <div class="font-weight-bold text-subtitle-1 mt-1">{{ totalQuantityDisplay }}</div>
        </div>

        <div v-if="pieSegments.length" class="d-flex align-center px-6 pt-2 pb-4">
          <svg width="180" height="180" viewBox="0 0 180 180" class="flex-shrink-0">
            <path
              v-for="seg in pieSegments"
              :key="'pie-' + seg.lotId"
              :d="seg.path"
              :fill="seg.color"
            />
          </svg>
          <div class="ml-6 po-quality-grading-legend">
            <div
              v-for="item in legendItems"
              :key="'legend-' + item.key"
              class="d-flex align-center mb-2"
            >
              <span
                class="po-quality-grading-legend-dot mr-2"
                :style="{ backgroundColor: item.color }"
              ></span>
              <span class="text-body-2">{{ item.label }}: {{ item.percentage }}%</span>
            </div>
          </div>
        </div>

        <div v-for="lot in localLots" :key="String(lot.id) + (lot.lotId || '')">
          <v-divider />
          <div class="px-6 py-5">
            <div class="d-flex align-center justify-space-between mb-3">
              <span class="font-weight-bold po-quality-grading-lot-id">
                {{ $t("memberDataSection.purchaseOrderDetail.lotId") }}: {{ lot.lotId || "—" }}
              </span>
              <v-btn
                fab
                x-small
                depressed
                color="primary"
                class="po-quality-grading-edit-btn white--text"
                elevation="0"
                @click="openAddQualityGrade(lot)"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </div>
            <v-row dense>
              <v-col cols="4">
                <div class="po-quality-grading-field-label">
                  {{ $t("memberDataSection.greenCoffee.quantity") }}
                </div>
                <div class="po-quality-grading-field-value">
                  {{ lot.quantity != null ? lot.quantity + " kg" : "—" }}
                </div>
              </v-col>
              <v-col cols="4">
                <div class="po-quality-grading-field-label">
                  {{ $t("memberDataSection.greenCoffee.packing") }}
                </div>
                <div class="po-quality-grading-field-value">
                  {{ lot.unitSize != null ? lot.unitSize + " kg Bag" : "—" }}
                </div>
              </v-col>
              <v-col cols="4">
                <div class="po-quality-grading-field-label">
                  {{ $t("memberDataSection.greenCoffee.totalBags") }}
                </div>
                <div class="po-quality-grading-field-value">
                  {{ displayTotalBags(lot) }}
                </div>
              </v-col>
              <v-col cols="4" class="mt-3">
                <div class="po-quality-grading-field-label">
                  {{ $t("memberDataSection.greenCoffee.leftoverQuantity") }}
                </div>
                <div class="po-quality-grading-field-value">
                  {{ displayLeftoverQuantity(lot) }}
                </div>
              </v-col>
              <v-col cols="4" class="mt-3">
                <div class="po-quality-grading-field-label">
                  {{ $t("memberDataSection.greenCoffee.qualityScore") }}
                </div>
                <div class="po-quality-grading-field-value">{{ lot.qualityScore || "—" }}</div>
              </v-col>
              <v-col cols="4" class="mt-3">
                <div class="po-quality-grading-field-label">
                  {{ $t("memberDataSection.greenCoffee.percentage") }}
                </div>
                <div class="po-quality-grading-field-value">
                  {{ lot.percentage != null ? lot.percentage + "%" : "—" }}
                </div>
              </v-col>
            </v-row>
          </div>
        </div>

        <div
          v-if="!localLots.length"
          class="px-6 py-6 text-center text--secondary text-body-2"
        >
          {{ $t("memberDataSection.greenCoffee.noLotsYet") }}
        </div>

        <v-divider />

        <div class="px-6 py-5">
          <v-btn
            outlined
            color="primary"
            height="36"
            class="text-none font-weight-bold"
            @click="openAddQualityGrade(null)"
          >
            <v-icon left small>mdi-seal</v-icon>
            {{ $t("memberDataSection.greenCoffee.createQualityGrade") }}
          </v-btn>
        </div>

        <v-divider />

        <div class="px-6 py-5">
          <div class="font-weight-bold text-body-1 mb-2">
            {{ $t("memberDataSection.greenCoffee.finishGrading") }}
          </div>
          <div class="text-body-2 text--secondary">
            {{ $t("memberDataSection.greenCoffee.finishGradingDesc") }}
          </div>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-6 py-4">
        <v-spacer></v-spacer>
        <v-btn
          outlined
          color="primary"
          height="36"
          class="text-uppercase px-6"
          @click="close"
        >
          {{ $t("cancel") }}
        </v-btn>
        <v-btn
          depressed
          color="primary"
          height="36"
          class="text-uppercase white--text px-6 ml-2"
          @click="finishGrading"
        >
          {{ $t("memberDataSection.greenCoffee.finishGradingButton") }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <PurchaseOrderAddQualityGradeDialog
      v-model="addGradeDialogVisible"
      :lot="selectedLot"
      :max-quantity="gradingSupplyQuantity"
      :suggested-lot-id="suggestedLotId"
      :existing-lot-ids="existingLotIds"
      :default-unit-size="unitSize"
      :quality-scores="qualityScores"
      :labels="labels"
      :grade-options="gradeOptions"
      @submit="onQualityGradeCreated"
    />
  </v-dialog>
</template>

<script>
import PurchaseOrderAddQualityGradeDialog from "./AddQualityGradeDialog.vue";

const CHART_COLOR_FIRST = "#0EBF67";
const CHART_COLOR_SECOND = "#FC863B";
const CHART_UNGRADED_COLOR = "#BBBBBB";
const CHART_COLOR_SEQUENCE = [
  CHART_COLOR_FIRST,
  CHART_COLOR_SECOND,
  "#1976d2",
  "#7b1fa2",
  "#c62828",
  "#00838f",
  "#5d4037",
  "#5c6bc0",
  "#ad1457",
  "#3949ab",
  "#F9A825",
];

export default {
  name: "PurchaseOrderQualityGradingDialog",
  components: { PurchaseOrderAddQualityGradeDialog },
  props: {
    value: { type: Boolean, default: false },
    totalQuantity: { type: [Number, String], default: 0 },
    unitSize: { type: [Number, String], default: 40 },
    lots: { type: Array, default: () => [] },
    qualityScores: { type: Array, default: () => [] },
    labels: { type: Array, default: () => [] },
    gradeOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      visible: this.value,
      addGradeDialogVisible: false,
      selectedLot: null,
      localLots: [],
    };
  },
  computed: {
    totalQuantityDisplay() {
      const q = Number(this.totalQuantity) || 0;
      return q ? `${q} kg` : "—";
    },
    gradingSupplyQuantity() {
      const total = Number(this.totalQuantity) || 0;
      let usedByOthers = this.localLots.reduce(
        (sum, l) => sum + (Number(l.quantity) || 0),
        0
      );
      if (this.selectedLot) {
        usedByOthers -= Number(this.selectedLot.quantity) || 0;
      }
      return Math.max(0, total - usedByOthers);
    },
    existingLotIds() {
      return this.localLots.map((l) => l.lotId).filter(Boolean);
    },
    suggestedLotId() {
      const numericIds = this.localLots
        .map((l) => parseInt(String(l.lotId || "").replace(/\D/g, ""), 10))
        .filter((n) => !Number.isNaN(n));
      if (!numericIds.length) return "";
      return String(Math.max(...numericIds) + 1);
    },
    gradingBaseTotalKg() {
      return Number(this.totalQuantity) || 0;
    },
    gradedQuantityKg() {
      return (this.localLots || []).reduce(
        (sum, l) => sum + (Number(l.quantity) || 0),
        0
      );
    },
    ungradedQuantityKg() {
      return Math.max(0, this.gradingBaseTotalKg - this.gradedQuantityKg);
    },
    chartSlices() {
      const base = this.gradingBaseTotalKg;
      if (!base) return [];

      const slices = [];
      let previousColor = null;
      (this.localLots || []).forEach((lot, index) => {
        const qty = Number(lot.quantity) || 0;
        if (qty <= 0) return;
        const color = this.chartColorForLotIndex(index, previousColor);
        previousColor = color;
        slices.push({
          lotId: lot.lotId || `lot-${index}`,
          label: lot.lotId || "—",
          share: qty / base,
          color,
          isRemainder: false,
        });
      });

      const normalizedLots = this.ensureDistinctAdjacentChartColors(
        slices.map((slice) => ({ ...slice }))
      );

      const ungraded = this.ungradedQuantityKg;
      if (ungraded > 0) {
        normalizedLots.push({
          lotId: "__ungraded__",
          label: "",
          share: ungraded / base,
          color: CHART_UNGRADED_COLOR,
          isRemainder: true,
        });
      }
      return normalizedLots;
    },
    legendItems() {
      return this.chartSlices
        .filter((slice) => !slice.isRemainder)
        .map((slice) => ({
          key: slice.lotId,
          label: slice.label,
          percentage: this.formatChartPercent(slice.share * 100),
          color: slice.color,
        }));
    },
    pieSegments() {
      const cx = 90;
      const cy = 90;
      const r = 82;
      const paths = [];
      let currentAngle = -90;

      for (const slice of this.chartSlices) {
        if (slice.share <= 0) continue;
        const sweep = slice.share * 360;
        paths.push({
          lotId: slice.lotId,
          color: slice.color,
          path: this.describeSlice(cx, cy, r, currentAngle, currentAngle + sweep),
        });
        currentAngle += sweep;
      }
      return paths;
    },
  },
  watch: {
    value(v) {
      this.visible = v;
      if (v) this.localLots = [...(this.lots || [])];
    },
    visible(v) {
      this.$emit("input", v);
    },
    lots: {
      handler(val) {
        this.localLots = [...(val || [])];
      },
      immediate: true,
    },
  },
  methods: {
    chartColorForLotIndex(index, previousColor) {
      if (index === 0) return CHART_COLOR_SEQUENCE[0];
      if (index === 1) return CHART_COLOR_SEQUENCE[1];

      const pool = CHART_COLOR_SEQUENCE.slice(2);
      const offset = index - 2;
      let color = pool[offset % pool.length];
      if (color === previousColor) {
        color = pool[(offset + 1) % pool.length];
      }
      return color;
    },
    ensureDistinctAdjacentChartColors(slices) {
      if (slices.length < 2) return slices;

      const pool = CHART_COLOR_SEQUENCE.filter((c) => c !== CHART_UNGRADED_COLOR);
      const pickOther = (avoid, alsoAvoid) =>
        pool.find((c) => c !== avoid && c !== alsoAvoid) ||
        pool.find((c) => c !== avoid) ||
        pool[0];

      for (let i = 1; i < slices.length; i += 1) {
        if (slices[i].color === slices[i - 1].color) {
          slices[i].color = pickOther(slices[i - 1].color);
        }
      }

      const lastIdx = slices.length - 1;
      if (slices[0].color === slices[lastIdx].color) {
        const neighbor = slices[lastIdx - 1]?.color;
        slices[lastIdx].color = pickOther(slices[0].color, neighbor);
      }

      return slices;
    },
    displayTotalBags(lot) {
      if (lot.totalBags != null && lot.totalBags !== "") {
        return lot.totalBags;
      }
      const qty = Number(lot.quantity) || 0;
      const unit = Number(lot.unitSize) || 0;
      if (!qty || !unit) return "—";
      return Math.floor(qty / unit);
    },
    displayLeftoverQuantity(lot) {
      if (lot.leftoverQuantity != null && lot.leftoverQuantity !== "") {
        return `${lot.leftoverQuantity} kg`;
      }
      const qty = Number(lot.quantity) || 0;
      const unit = Number(lot.unitSize) || 0;
      if (!qty || !unit) return "—";
      return `${qty % unit} kg`;
    },
    formatChartPercent(value) {
      const n = Math.round(Number(value) * 10) / 10;
      if (!n) return "0";
      return Number.isInteger(n) ? String(n) : n.toFixed(1);
    },
    describeSlice(cx, cy, r, startDeg, endDeg) {
      if (endDeg - startDeg >= 359.999) {
        const topX = cx;
        const topY = cy - r;
        const bottomX = cx;
        const bottomY = cy + r;
        return `M ${cx} ${cy} L ${topX.toFixed(2)} ${topY.toFixed(2)} A ${r} ${r} 0 1 1 ${bottomX.toFixed(2)} ${bottomY.toFixed(2)} A ${r} ${r} 0 1 1 ${topX.toFixed(2)} ${topY.toFixed(2)} Z`;
      }
      const toRad = (deg) => (deg * Math.PI) / 180;
      const x1 = cx + r * Math.cos(toRad(startDeg));
      const y1 = cy + r * Math.sin(toRad(startDeg));
      const x2 = cx + r * Math.cos(toRad(endDeg));
      const y2 = cy + r * Math.sin(toRad(endDeg));
      const large = endDeg - startDeg > 180 ? 1 : 0;
      return `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
    },
    close() {
      this.visible = false;
      this.$emit("close");
    },
    openAddQualityGrade(lot) {
      this.selectedLot = lot || null;
      this.addGradeDialogVisible = true;
    },
    onQualityGradeCreated(grade) {
      this.addGradeDialogVisible = false;

      const newQty = Number(grade.quantity) || 0;
      const totalBase = Number(this.totalQuantity) || 0;

      let currentTotal = this.localLots.reduce(
        (sum, l) => sum + (Number(l.quantity) || 0),
        0
      );
      if (this.selectedLot) {
        const existingIdx = this.localLots.findIndex(
          (l) => l.id === this.selectedLot.id && l.lotId === this.selectedLot.lotId
        );
        if (existingIdx >= 0) {
          currentTotal -= Number(this.localLots[existingIdx].quantity) || 0;
        }
      }
      const tentativeTotal = currentTotal + newQty;

      if (totalBase > 0 && tentativeTotal > totalBase) {
        this.$notify({
          type: "error",
          title: this.$t("memberDataSection.greenCoffee.totalGreenBeansExceeded"),
        });
        this.selectedLot = null;
        return;
      }

      if (this.selectedLot) {
        const idx = this.localLots.findIndex(
          (l) => l.id === this.selectedLot.id && l.lotId === this.selectedLot.lotId
        );
        if (idx >= 0) {
          this.$set(this.localLots, idx, {
            ...this.localLots[idx],
            lotId: grade.lotId,
            quantity: grade.quantity,
            unitSize: grade.unitSize,
            totalBags: this.computeTotalBags(grade.quantity, grade.unitSize),
            leftoverQuantity: this.computeLeftover(grade.quantity, grade.unitSize),
            qualityScoreId: grade.qualityScoreId,
            qualityTitle: grade.qualityTitle,
            qualityScore: grade.qualityScore,
            label: grade.label,
          });
        } else {
          this.localLots.push(this.buildLot(grade));
        }
      } else {
        this.localLots.push(this.buildLot(grade));
      }

      this.recalculatePercentages();
      this.selectedLot = null;
      this.$emit("lots-changed", [...this.localLots]);
    },
    computeTotalBags(quantity, unitSize) {
      const qty = Number(quantity) || 0;
      const unit = Number(unitSize) || 0;
      if (!qty || !unit) return 0;
      return Math.floor(qty / unit);
    },
    computeLeftover(quantity, unitSize) {
      const qty = Number(quantity) || 0;
      const unit = Number(unitSize) || 0;
      if (!qty || !unit) return 0;
      return qty % unit;
    },
    buildLot(grade) {
      return {
        id: (grade.lotId || "") + "_" + Date.now(),
        lotId: grade.lotId,
        quantity: grade.quantity,
        unitSize: grade.unitSize,
        totalBags: this.computeTotalBags(grade.quantity, grade.unitSize),
        leftoverQuantity: this.computeLeftover(grade.quantity, grade.unitSize),
        qualityScoreId: grade.qualityScoreId,
        qualityTitle: grade.qualityTitle || "",
        qualityScore: grade.qualityScore || "",
        label: grade.label || "",
        percentage: null,
      };
    },
    recalculatePercentages() {
      const baseTotal = this.gradingBaseTotalKg;
      if (baseTotal <= 0) return;
      this.localLots = this.localLots.map((l) => {
        const qty = Number(l.quantity) || 0;
        return {
          ...l,
          percentage: this.formatChartPercent((qty / baseTotal) * 100),
        };
      });
    },
    finishGrading() {
      this.$emit("finish-grading", [...this.localLots]);
      this.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.po-quality-grading-card {
  font-family: "Poppins", sans-serif;
  background: #ffffff;
}

.po-quality-grading-card__body {
  max-height: calc(90vh - 120px);
  overflow-y: auto;
  background: #ffffff;
}

.po-quality-grading-legend {
  max-height: 180px;
  overflow-y: auto;
  min-width: 140px;
}

.po-quality-grading-legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.po-quality-grading-lot-id {
  color: #6d4c41;
  font-size: 1rem;
}

.po-quality-grading-edit-btn {
  width: 32px !important;
  height: 32px !important;
  box-shadow: none;
}

.po-quality-grading-field-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
  line-height: 1.4;
}

.po-quality-grading-field-value {
  font-weight: 700;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  margin-top: 2px;
}
</style>

<style>
.po-quality-grading-dialog {
  overflow: visible;
}

.po-quality-grading-dialog > .v-card {
  background: #ffffff !important;
}
</style>
